import { Directive, Injectable, inject } from '@angular/core';

import { EMPTY, Subject } from 'rxjs';

import type {
  ConfigPlugins,
  EditorButton,
  IProsemirrorConfig,
  PluginsButtons,
  PluginsEvents,
} from '@core/prosemirror-legacy';
import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  BoldPlugin,
  ContextMenuPlugin,
  EmojiEditorPlugin,
  ItalicPlugin,
  LinkEditPlugin,
  RtSchema,
  SCHEMAS,
  UnderlinePlugin,
} from '@core/prosemirror-legacy';
import { ProsemirrorBaseConfig, ScrollStream$ } from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';

import { ToolbarService } from '../components/toolbar/toolbar.service';
import { ContextToolbarService } from '../components/tooltips/context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';
import { ProsemirrorConfig } from '../providers';

export type ProsemirrorBasicConfigEvents = PluginsEvents<ConfigPlugins<ProsemirrorBasicConfig>>;

@Injectable()
export class ProsemirrorBasicConfig extends ProsemirrorBaseConfig implements IProsemirrorConfig {
  protected readonly symbolPickerService? = inject(SymbolPickerService, { optional: true });
  protected readonly toolbarService? = inject(ToolbarService, { optional: true });
  protected readonly contextMenuService? = inject(ContextToolbarService, { optional: true });
  protected readonly linkService? = inject(LinkComponentService, { optional: true });
  protected readonly scrollStream$? = inject(ScrollStream$, { optional: true });

  public eventStream$ = new Subject<ProsemirrorBasicConfigEvents>();

  private scroll$ = this.scrollStream$ || EMPTY;

  public getSchema() {
    return SCHEMAS[RtSchema.Basic](document);
  }

  public getRenderPlugins() {
    return [];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new ContextMenuPlugin(this.contextMenuService!, this.scroll$),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),

      new BoldPlugin(),
      new ItalicPlugin(),
      new UnderlinePlugin(),
      new LinkEditPlugin(this.scroll$, this.linkService!),
      new AutoLinkWrapPlugin(),
    ];
  }

  public registerToolbarButtons(buttons: PluginsButtons<ConfigPlugins<ProsemirrorBasicConfig>>) {
    const ctxButtons = [
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleLink,
    ];
    const leftButtons = [
      [
        buttons.toggleBold,
        buttons.toggleItalic,
        buttons.toggleUnderline,
        buttons.toggleLink,
        buttons.undo,
        buttons.redo,
      ],
    ];

    const rightButtons: EditorButton[] = [buttons.toggleEmojiList];
    this.toolbarService!.setContextButtons(ctxButtons);
    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
  }
}

@Directive({
  selector: '[prosemirrorBasicConfig]',
  providers: [
    ProsemirrorBasicConfig,
    { provide: ProsemirrorConfig, useValue: ProsemirrorBasicConfig },
  ],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorBasicConfigDirective {}
